import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Award, CheckCircle2, Clock, MapPin, Phone, ShieldCheck } from "lucide-react";
import Reveal from "@/components/SectionReveal";
import QuoteForm from "@/components/QuoteForm";
import Testimonials from "@/components/Testimonials";
import { CITIES, SERVICES, SITE } from "@/data/site";
import { useSeo, localBusinessJsonLd } from "@/lib/seo";

export default function CityPage() {
  const { city } = useParams();
  const data = CITIES.find((c) => c.slug === city);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [city]);

  useSeo({
    title: data
      ? `Pool Cleaning & Service in ${data.name}, FL — Free Quotes, 24/7 Emergency`
      : "Service Area",
    description: data
      ? `Looking for a trusted pool cleaner in ${data.name}, FL? Tampa Bay Pool Pros offers weekly pool service, green pool cleanup, filter & pump cleaning, equipment repair, and 24/7 pool emergency response in ${data.name}. 25+ years experience. Free quotes.`
      : "",
    path: data ? `/service-areas/${data.slug}` : "/service-areas",
    image: SITE.logo,
    jsonLd: data
      ? localBusinessJsonLd({
          name: `${SITE.name} — ${data.name}`,
          areaServed: [`${data.name}, FL`],
        })
      : undefined,
  });

  if (!data) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative pt-32 sm:pt-40 pb-20 bg-gradient-to-br from-sky-50 via-white to-amber-50/40 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <Link
              to="/service-areas"
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-sky-700"
              data-testid="city-back-link"
            >
              <ArrowLeft className="w-4 h-4" /> All service areas
            </Link>
          </Reveal>

          <Reveal>
            <p className="mt-6 text-xs tracking-[0.22em] uppercase font-bold text-sky-700 inline-flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> {data.name}, Florida
            </p>
            <h1
              className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 max-w-4xl"
              data-testid="city-h1"
            >
              Pool cleaning &amp; service in <span className="text-sky-600">{data.name}, FL</span>
            </h1>
            <p className="mt-5 max-w-2xl text-slate-700 text-lg">
              {data.blurb} Tampa Bay Pool Pros delivers reliable weekly pool service, green pool
              cleanup, filter &amp; pump cleaning, chemical balancing, equipment repair, and 24/7
              pool emergency response throughout {data.name}.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#quote"
                data-testid="city-cta-quote"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-slate-900 font-bold hover:bg-amber-300"
              >
                Free quote in {data.name}
              </a>
              <a
                href={`tel:${SITE.phoneTel}`}
                data-testid="city-cta-call"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white font-semibold hover:bg-sky-700"
              >
                <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
              </a>
            </div>

            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-700">
              <li className="inline-flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-500" /> {SITE.experience} experience
              </li>
              <li className="inline-flex items-center gap-2">
                <Clock className="w-4 h-4 text-sky-600" /> 24/7 emergency
              </li>
              <li className="inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" /> Licensed &amp; insured
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Services in this city */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-sky-700">
              {data.name} Pool Services
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-slate-900 max-w-3xl">
              Our full range of pool services in {data.name}.
            </h2>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s, i) => (
              <Reveal
                key={s.slug}
                delay={i * 0.04}
                direction={i % 2 === 0 ? "up" : "left"}
                className="rounded-3xl bg-white border border-slate-100 p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:-translate-y-1 transition-transform"
                testId={`city-service-${s.slug}`}
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-500 shrink-0" />
                  <div>
                    <h3 className="font-heading font-bold text-slate-900">
                      {s.title} in {data.name}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">{s.blurb}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SEO long-form content */}
      <section className="py-20 sm:py-24 bg-sky-50/40">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 prose prose-slate">
          <Reveal>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
              Trusted local pool pros in {data.name}, FL
            </h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Owning a pool in {data.name} should feel like a perk — not a chore. Florida humidity,
              afternoon thunderstorms, and pollen from local trees can throw any pool out of balance
              fast. That&apos;s where Tampa Bay Pool Pros comes in. Since establishing our family
              business in {SITE.established} (with {SITE.experience} of hands-on industry
              experience behind us), we&apos;ve helped hundreds of {data.name} homeowners keep their
              pools spotless, safe, and ready to swim every weekend.
            </p>
            <p className="mt-4 text-slate-700 leading-relaxed">
              From routine weekly pool cleaning and chemical balancing to advanced green pool
              cleanup, pump &amp; filter service, and 24/7 pool emergency response, our {data.name}{" "}
              pool techs do it all. Need a brand-new pool system? Ask about our turnkey
              <strong> &ldquo;Pool Systems in a Box&rdquo;</strong> bundles delivered through our reputable
              partnered contractors.
            </p>
            <h3 className="mt-8 font-heading text-xl font-bold text-slate-900">
              Why {data.name} homeowners choose us
            </h3>
            <ul className="mt-4 space-y-2">
              <li>✔ Local Tampa Bay company — fast response across {data.name}</li>
              <li>✔ 25+ years of pool industry experience</li>
              <li>✔ Honest, flat-rate weekly pool service pricing</li>
              <li>✔ 24/7 emergency line for pump failures, leaks, and storm damage</li>
              <li>✔ Licensed, insured, and friendly</li>
            </ul>
          </Reveal>
        </div>
      </section>

      <Testimonials />

      <section id="quote" className="py-16 sm:py-20 bg-gradient-to-b from-sky-50/40 to-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <QuoteForm presetCity={data.name} />
        </div>
      </section>
    </main>
  );
}
